/**
 * Mindbricks-standard system event names. All auto-emitted events use a
 * `$` prefix so they're trivially distinguishable from user/business events
 * like `payment_completed` or `cart_updated`.
 *
 * The seven canonical events the dashboards rely on:
 *   $page_view, $session_start, $session_end, $click,
 *   $form_submit, $error, $performance
 *
 * Supplementary events ($page_enter, $scroll_depth, $rage_click, …) are
 * kept on the same prefix for consistency. They are richer signals that
 * deeper product-analytics views can opt into.
 */
export const AUTO_EVENTS = {
  // --- canonical 7 ---------------------------------------------------------
  PAGE_VIEW: "$page_view",
  SESSION_START: "$session_start",
  SESSION_END: "$session_end",
  CLICK: "$click",
  FORM_SUBMIT: "$form_submit",
  ERROR: "$error",
  PERFORMANCE: "$performance",

  // --- supplementary signals ----------------------------------------------
  PAGE_ENTER: "$page_enter",
  PAGE_LEAVE: "$page_leave",
  TAB_HIDDEN: "$tab_hidden",
  TAB_VISIBLE: "$tab_visible",
  FORM_START: "$form_start",
  FORM_ABANDON: "$form_abandon",
  INPUT_FOCUS: "$input_focus",
  SCROLL_DEPTH: "$scroll_depth",
  RAGE_CLICK: "$rage_click",
  DEAD_CLICK: "$dead_click",
  NETWORK_ONLINE: "$network_online",
  NETWORK_OFFLINE: "$network_offline",
  IDENTIFY: "$identify",
} as const;

export type AutoEventName = (typeof AUTO_EVENTS)[keyof typeof AUTO_EVENTS];

/**
 * Critical business events. The ingestion API rejects these on the
 * untrusted (frontend) endpoint — they may only arrive via the trusted
 * server-side endpoint, signed with the project's secretKey.
 *
 * Business events deliberately do NOT use the `$` prefix; that prefix is
 * reserved for SDK-emitted system events.
 */
export const SERVER_ONLY_EVENTS: ReadonlySet<string> = new Set([
  "payment_completed",
  "order_created",
  "subscription_started",
  "user_registered",
  "plan_upgraded",
  "credit_spent",
  "quota_used",
]);

export const TRAFFIC_SOURCE = {
  DIRECT: "direct",
  REFERRAL: "referral",
  ORGANIC_SEARCH: "organic_search",
  PAID_SEARCH: "paid_search",
  SOCIAL: "social",
  EMAIL: "email",
  UNKNOWN: "unknown",
} as const;

export type TrafficSource = (typeof TRAFFIC_SOURCE)[keyof typeof TRAFFIC_SOURCE];

/** Web Vitals metric names emitted as the `metric` property of $performance. */
export const PERFORMANCE_METRICS = {
  LCP: "lcp",
  FID: "fid",
  CLS: "cls",
  TTFB: "ttfb",
  INP: "inp",
  FCP: "fcp",
} as const;

export type PerformanceMetric = (typeof PERFORMANCE_METRICS)[keyof typeof PERFORMANCE_METRICS];
